import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  LeadCompetitionData,
  LeadGroup,
  LeadAthlete,
  LeadScore,
  AthleteQualRow,
  AthleteFinalsRow,
  formatLeadScore,
  formatRank,
  formatCombined,
  computeAllGroupQualRankings,
  computeFinalsRankings,
  computeFinalsRankingsByGroup,
} from '../utils/leadCompetition';
import { subscribeLeadData } from '../firebase/leadService';
import { ATHLETE_BY_NAME } from '../data/athletes';
import ClassBadge from '../components/ClassBadge';

// ─── helpers ──────────────────────────────────────────────────────────────────

const entered = (s: LeadScore | undefined) => !!s && (s.top || s.dnf || s.holds !== null);

// ─── AthleteLink ──────────────────────────────────────────────────────────────

const AthleteLink: React.FC<{ name: string; children: React.ReactNode }> = ({ name, children }) => {
  const slug = ATHLETE_BY_NAME[name]?.id;
  if (!slug) return <>{children}</>;
  return <Link to={`/athletes/${slug}`} className="hover:text-primary hover:underline transition-colors">{children}</Link>;
};

// ─── LeadBadge ────────────────────────────────────────────────────────────────

const trunc = (s: string, n = 9) => s.length > n ? s.slice(0, n) + '…' : s;

const LeadBadge: React.FC<{ isLead: boolean }> = ({ isLead }) => {
  if (!isLead) return null;
  return (
    <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ml-1.5 bg-blue-100 text-blue-700">
      LEAD
    </span>
  );
};

// ─── QualGroupTable ───────────────────────────────────────────────────────────

const CLASS_ORDER = ['AL1','AL2','AU3','B1','B2','B3','RP1','RP2','RP3'];

const GroupSubtitle: React.FC<{ groupId: string; rows: Array<{ athlete: LeadAthlete }>; subColor?: string }> = ({ groupId, rows, subColor = 'text-blue-200' }) => {
  const genderLabel = groupId.startsWith('M') ? 'Men' : groupId.startsWith('W') ? 'Women' : '';
  const classes = Array.from(new Set(rows.map(r => r.athlete.classId)))
    .sort((a, b) => CLASS_ORDER.indexOf(a) - CLASS_ORDER.indexOf(b));
  if (!genderLabel && classes.length === 0) return null;
  return (
    <p className={`${subColor} text-sm mt-0.5 flex items-center gap-1 flex-wrap`}>
      {genderLabel && <span>{genderLabel}</span>}
      {genderLabel && classes.length > 0 && <span>·</span>}
      {classes.map((cls, i) => (
        <React.Fragment key={cls}>
          <ClassBadge classId={cls} variant="on-dark" />
          {i < classes.length - 1 && <span>,</span>}
        </React.Fragment>
      ))}
    </p>
  );
};

const QualGroupTable: React.FC<{
  group: LeadGroup | null;
  groupId: string;
  rows: AthleteQualRow[];
  gender?: 'men' | 'women';
}> = ({ group, groupId, rows, gender }) => {
  const anyScores = rows.some(r => r.value1 > -1 || r.value2 > -1);
  const headerBg = gender === 'women' ? 'bg-pink-600' : gender === 'men' ? 'bg-blue-700' : 'bg-secondary';
  const subColor  = gender === 'women' ? 'text-pink-100' : 'text-blue-200';

  return (
    <section className="mb-8">
      <div className={`${headerBg} text-white px-5 py-3 rounded-t-lg flex items-center justify-between`}>
        <div>
          <h2 className="text-xl font-bold tracking-wide">{group?.name ?? `Group "${groupId}"`}</h2>
          <GroupSubtitle groupId={groupId} rows={rows} subColor={subColor} />
        </div>
        <span className={`${subColor} text-sm shrink-0 ml-4`}>{rows.length} athletes</span>
      </div>
      <div className="bg-white rounded-b-lg shadow overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
              <th className="px-4 py-2 text-center w-12">Rank</th>
              <th className="px-4 py-2 text-left">Athlete</th>
              <th className="px-4 py-2 text-center w-24 border-l border-gray-200">Route 1</th>
              <th className="px-4 py-2 text-center w-24 border-l border-gray-200">Route 2</th>
              <th className="px-4 py-2 text-center w-24 border-l border-gray-200">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const entered = (s: LeadScore | undefined) => !!s && (s.top || s.dnf || s.holds !== null);
              const hasScore = entered(row.score1) || entered(row.score2);
              return (
                <tr key={row.athlete.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${i === 0 && anyScores ? 'bg-yellow-50' : ''}`}>
                  <td className="px-4 py-3 text-center font-bold text-secondary">{hasScore ? formatRank(row.finalRank) : '—'}</td>
                  <td className="px-4 py-3 font-semibold text-dark">
                    <AthleteLink name={`${row.athlete.firstName} ${row.athlete.lastName}`}>
                      {trunc(row.athlete.firstName)} {trunc(row.athlete.lastName)}
                    </AthleteLink>
                    {row.athlete.country && <span className="ml-1.5 text-xs text-gray-400 font-normal">({row.athlete.country})</span>}
                    <LeadBadge isLead={row.athlete.isLead} />
                    <span className="ml-1.5 text-xs text-gray-400 font-normal"><ClassBadge classId={row.athlete.classId} /></span>
                  </td>
                  <td className={`px-4 py-3 text-center border-l border-gray-100 ${
                    row.score1?.top ? 'text-green-700 bg-green-50'
                    : row.score1?.dnf ? 'text-red-500'
                    : row.value1 > -1 ? 'text-blue-700'
                    : 'text-gray-300'
                  }`}>
                    <div className="font-mono font-bold">{formatLeadScore(row.score1)}</div>
                    {entered(row.score1) && <div className="text-[10px] text-gray-400 font-normal">#{formatRank(row.rank1)}</div>}
                  </td>
                  <td className={`px-4 py-3 text-center border-l border-gray-100 ${
                    row.score2?.top ? 'text-green-700 bg-green-50'
                    : row.score2?.dnf ? 'text-red-500'
                    : entered(row.score2) ? 'text-blue-700'
                    : 'text-gray-300'
                  }`}>
                    <div className="font-mono font-bold">{formatLeadScore(row.score2)}</div>
                    {entered(row.score2) && <div className="text-[10px] text-gray-400 font-normal">#{formatRank(row.rank2)}</div>}
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-primary border-l border-gray-100">
                    {hasScore ? formatCombined(row.combined) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// ─── FinalsGroupTable ─────────────────────────────────────────────────────────

const FinalsGroupTable: React.FC<{
  group: LeadGroup | null;
  groupId: string;
  rows: AthleteFinalsRow[];
  gender?: 'men' | 'women';
}> = ({ group, groupId, rows, gender }) => {
  const headerBg  = gender === 'women' ? 'bg-pink-600' : gender === 'men' ? 'bg-blue-700' : 'bg-dark';
  const subColor  = gender === 'women' ? 'text-pink-100' : 'text-gray-300';
  return (
  <section className="mb-8">
    <div className={`${headerBg} text-white px-5 py-3 rounded-t-lg flex items-center justify-between`}>
      <div>
        <h2 className="text-xl font-bold tracking-wide">{group?.name ?? `Group "${groupId}"`}</h2>
        <GroupSubtitle groupId={groupId} rows={rows} subColor={subColor} />
      </div>
      <span className={`${subColor} text-sm shrink-0 ml-4`}>{rows.length} finalists</span>
    </div>
    <div className="bg-white rounded-b-lg shadow overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
            <th className="px-4 py-2 text-center w-16">Rank</th>
            <th className="px-4 py-2 text-left">Athlete</th>
            <th className="px-4 py-2 text-center w-28">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.athlete.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 text-center font-bold text-secondary text-lg">
                {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : formatRank(row.rank)}
              </td>
              <td className="px-4 py-3 font-semibold text-dark">
                <AthleteLink name={`${row.athlete.firstName} ${row.athlete.lastName}`}>
                  {trunc(row.athlete.firstName)} {trunc(row.athlete.lastName)}
                </AthleteLink>
                {row.athlete.country && <span className="ml-1.5 text-xs text-gray-400 font-normal">({row.athlete.country})</span>}
                <LeadBadge isLead={row.athlete.isLead} />
                <span className="ml-1.5 text-xs text-gray-400 font-normal"><ClassBadge classId={row.athlete.classId} /></span>
              </td>
              <td className={`px-4 py-3 text-center font-mono font-bold text-xl ${
                row.score?.top ? 'text-green-700' : row.score?.dnf ? 'text-red-500' : 'text-blue-700'
              }`}>
                {formatLeadScore(row.score)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
  );
};

// ─── QualifiersTable ──────────────────────────────────────────────────────────

const QualifiersTable: React.FC<{
  group: LeadGroup | null;
  groupId: string;
  rows: AthleteQualRow[];
  spots: number;
  gender?: 'men' | 'women';
}> = ({ group, groupId, rows, spots, gender }) => {
  const headerBg = gender === 'women' ? 'bg-pink-600' : gender === 'men' ? 'bg-blue-700' : 'bg-dark';
  const subColor = gender === 'women' ? 'text-pink-100' : 'text-gray-300';
  return (
  <section className="mb-8">
    <div className={`${headerBg} text-white px-5 py-3 rounded-t-lg flex items-center justify-between`}>
      <div>
        <h2 className="text-xl font-bold tracking-wide">{group?.name ?? `Group "${groupId}"`}</h2>
        <GroupSubtitle groupId={groupId} rows={rows} subColor={subColor} />
      </div>
      <span className={`${subColor} text-sm shrink-0 ml-4`}>Top {spots} qualifiers</span>
    </div>
    <div className="bg-white rounded-b-lg shadow overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
            <th className="px-4 py-2 text-center w-16">Starting order</th>
            <th className="px-4 py-2 text-left">Athlete</th>
            <th className="px-4 py-2 text-center w-28 border-l border-gray-200">Total score</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].reverse().map(row => (
            <tr key={row.athlete.id} className="border-b border-gray-100 hover:bg-gray-50 bg-green-50">
              <td className="px-4 py-3 text-center font-bold text-secondary text-lg">
                {formatRank(row.finalRank)}
              </td>
              <td className="px-4 py-3 font-semibold text-dark">
                <AthleteLink name={`${row.athlete.firstName} ${row.athlete.lastName}`}>
                  {trunc(row.athlete.firstName)} {trunc(row.athlete.lastName)}
                </AthleteLink>
                {row.athlete.country && <span className="ml-1.5 text-xs text-gray-400 font-normal">({row.athlete.country})</span>}
                <LeadBadge isLead={row.athlete.isLead} />
                <span className="ml-1.5 text-xs text-gray-400 font-normal"><ClassBadge classId={row.athlete.classId} /></span>
              </td>
              <td className="px-4 py-3 text-center font-bold text-primary border-l border-gray-100">
                {formatCombined(row.combined)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
  );
};

// ─── LeadResultsPage ──────────────────────────────────────────────────────────

type Tab = 'women-qual' | 'women-finals' | 'men-qual' | 'men-finals';

const LeadResultsPage: React.FC = () => {
  const navigate    = useNavigate();
  const { competitionId = 'bk2026' } = useParams<{ competitionId: string }>();
  const [data,       setData]       = useState<LeadCompetitionData | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [connError,  setConnError]  = useState(false);
  const [activeTab,  setActiveTab]  = useState<Tab>('women-qual');

  useEffect(() => {
    const unsub = subscribeLeadData(
      competitionId,
      d => { setData(d); setLastUpdate(new Date()); setConnError(false); },
      () => setConnError(true),
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  if (!data) {
    if (connError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-dark text-white text-center px-4">
          <div>
            <div className="text-4xl mb-4">⚠</div>
            <p className="text-xl font-semibold">Could not connect to Firestore</p>
            <p className="text-gray-400 mt-2 text-sm">Check your internet connection or Firestore security rules.</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-secondary rounded text-sm">Retry</button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark text-white text-2xl">
        Loading…
      </div>
    );
  }

  const allGroupRankings      = computeAllGroupQualRankings(data);
  const finalsRankings         = computeFinalsRankings(data);
  const finalsRankingsByGroup  = computeFinalsRankingsByGroup(data);

  // Qualifiers: groups where every athlete has at least a qual1 score
  const qualifiersByGroup = allGroupRankings
    .map(({ group, groupId, rows }) => {
      if (rows.length === 0) return null;
      const allScored = rows.every(r => entered(r.score1));
      if (!allScored) return null;
      const spots = group?.finalSpots ?? 3;
      const qualRows = rows.filter(r => r.finalRank <= spots);
      return { group, groupId, qualRows, spots };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const genderOrder = (g: LeadGroup | null) =>
    g?.gender === 'women' ? 0 : g?.gender === 'men' ? 1 : 2;

  const sortedQualRankings = [...allGroupRankings]
    .sort((a, b) => genderOrder(a.group) - genderOrder(b.group));
  const sortedFinalsRankings = [...finalsRankingsByGroup]
    .sort((a, b) => genderOrder(a.group) - genderOrder(b.group));
  const sortedQualifiersByGroup = [...qualifiersByGroup]
    .sort((a, b) => genderOrder(a.group) - genderOrder(b.group));

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero header */}
      <div className="bg-gradient-to-r from-dark to-secondary text-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(`/competition/${competitionId}`)} className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-5 transition-colors">
            ← Competition hub
          </button>
        </div>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-2">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Lead</h1>
            <p className="text-blue-200 mt-1 text-lg">Live Results</p>
          </div>
          <div className="text-right text-xs text-blue-300">
            {connError ? (
              <span className="text-red-300">⚠ Connection error</span>
            ) : (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />
                Live · updated {lastUpdate.toLocaleTimeString()}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-gray-200 rounded-xl p-1 w-fit">
          {([
            { id: 'women-qual',   label: 'Women — Qualifications' },
            { id: 'women-finals', label: 'Women — Finals' },
            { id: 'men-qual',     label: 'Men — Qualifications' },
            { id: 'men-finals',   label: 'Men — Finals' },
          ] as { id: Tab; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-dark shadow'
                  : 'text-gray-500 hover:text-dark'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {(() => {
          const isWomen  = activeTab === 'women-qual' || activeTab === 'women-finals';
          const isQual   = activeTab === 'women-qual' || activeTab === 'men-qual';
          const byGender = (g: LeadGroup | null) =>
            isWomen ? g?.gender === 'women' : g?.gender !== 'women';

          if (isQual) {
            const filtered = sortedQualRankings.filter(r => byGender(r.group));
            return (
              <>
                <div className="mb-6 flex flex-wrap gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded bg-green-50 border border-green-300" />
                    TOP = topped the route (best)
                  </span>
                  <span><strong>Total = √(R1×R2)</strong> — lower is better</span>
                </div>
                {filtered.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
                    <p className="text-lg">Results will appear here once the competition starts.</p>
                  </div>
                ) : (
                  filtered.map(({ group, groupId, rows }) => (
                    <QualGroupTable key={groupId} group={group} groupId={groupId} rows={rows} gender={group?.gender} />
                  ))
                )}
              </>
            );
          }

          // Finals tab
          const filteredFinals = sortedFinalsRankings.filter(r => byGender(r.group));
          const filteredQuals  = sortedQualifiersByGroup.filter(r => byGender(r.group));
          if (filteredFinals.length > 0) {
            return filteredFinals.map(({ group, groupId, rows }) => (
              <FinalsGroupTable key={groupId} group={group} groupId={groupId} rows={rows} gender={group?.gender} />
            ));
          }
          if (filteredQuals.length > 0) {
            return (
              <>
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg px-5 py-3 text-sm text-blue-800">
                  Qualifications complete — finals have not started yet. These athletes advance to the finals route.
                </div>
                {filteredQuals.map(({ group, groupId, qualRows, spots }) => (
                  <QualifiersTable key={groupId} group={group} groupId={groupId} rows={qualRows} spots={spots} gender={group?.gender} />
                ))}
              </>
            );
          }
          return (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
              <p className="text-lg">Finals results will appear here once qualifications are complete.</p>
            </div>
          );
        })()}

      </div>
    </div>
  );
};

export default LeadResultsPage;
